import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

import Filter from 'components/Filter/Filter';
import Legend from 'components/Legend/Legend';
import Pagination from 'components/Pagination/Pagination';
import DirectionIcon from 'components/DirectionIcon/DirectionIcon';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './UsersIndex.scss';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  TableContainer,
  Tag,
  Progress,
  Flex,
} from '@chakra-ui/react';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useGetUsersQuery } from 'modules/users/usersApi';

export default ({ setSearchLegend }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Selectors
  const sessionUser = useSelector(state => state.app.user);

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  // Raw search input (not debounced)
  const [searchInput, setSearchInput] = useState('');

  // Debounced search parameters
  const [defaultSearchParameters, setDefaultSearchParameters] = useState({
    search: '',
    sort: [],
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        setDefaultSearchParameters(prev => ({ ...prev, search: query }));
        setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
      }, 500), // Adjust debounce delay
    [] // No dependencies, so it's created only once
  );

  // Wrap it in useCallback so it doesn't change on every render
  const handleDebouncedSearch = useCallback(
    query => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const {
    data: usersData,
    error,
    isLoading,
    isFetching,
  } = useGetUsersQuery({
    ...defaultSearchParameters,
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    sort: defaultSearchParameters.sort || [],
  });

  const columns = [
    {
      accessorKey: 'firstname',
      header: 'First Name',
      cell: props => <Text>{props.getValue()}</Text>,
    },
    {
      accessorKey: 'lastname',
      header: 'Last Name',
      cell: props => <Text>{props.getValue()}</Text>,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: props => {
        const isSessionUser = props.row.original.id === sessionUser?.id;
        return (
          <Text>
            {props.getValue()}
            {isSessionUser && (
              <Tag
                background={colors.primary900}
                color={colors.white}
                marginLeft={'8px'}
                borderRadius={'4px'}
              >
                Me
              </Tag>
            )}
          </Text>
        );
      },
    },
  ];

  const table = useReactTable({
    data: usersData?.data.entities || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // Enable manual pagination
    state: {
      pagination,
    },
  });

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch user data',
      })
    );
  }

  useEffect(() => {
    setSearchLegend(<Legend color={colors.status00} label="Active" />);

    return () => {
      setSearchLegend(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle sort clicked
  const handleSortClick = header => {
    if (header.column.getNextSortingOrder() === 'asc') {
      header.column.toggleSorting(false); // Set to 'asc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'asc',
          },
        ],
      });
    } else if (header.column.getNextSortingOrder() === 'desc') {
      header.column.toggleSorting(true); // Set to 'desc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'desc',
          },
        ],
      });
    } else {
      header.column.clearSorting(); // Remove sorting
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [],
      });
    }
  };

  // Handle rows per clicked
  const handleRowClick = row => {
    const userId = row.original.id;
    const isSessionUser = userId === sessionUser?.id;
    setSelectedRowId(prev => (prev === row.id ? null : row.id)); // Toggle selection

    if (isSessionUser) {
      navigate('/user/me');
    } else {
      navigate(`/users/${userId}`);
    }
  };

  return (
    <Box className="users-body">
      <Box height="60px" className="rail rail-rule">
        <Filter
          placeholder="Search users"
          value={searchInput}
          onChange={event => {
            setSearchInput(event); // Update raw input immediately
            handleDebouncedSearch(event); // Trigger debounced update
          }}
        />
      </Box>

      <Box className="users-table" borderWidth="1px">
        <TableContainer>
          {(isLoading || isFetching) && <Progress size="xs" isIndeterminate />}
          <Table size="md" variant="simple">
            <Thead zIndex={1} bg="white">
              {table.getHeaderGroups().map(headerGroup => (
                <Tr className="users-table__row" key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    const textAlign =
                      header.column.columnDef.textAlign || 'left';
                    return (
                      <Th
                        className="users-table__header"
                        key={header.id}
                        whiteSpace="nowrap"
                        textAlign={textAlign}
                      >
                        {header.column.getCanSort() ? (
                          <Flex justify={textAlign} align="center">
                            <Text
                              className="users-table__header-text"
                              mr={2}
                              onClick={() => handleSortClick(header)}
                              cursor={
                                header.column.getCanSort()
                                  ? 'pointer'
                                  : 'default'
                              }
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </Text>

                            <Box
                              onClick={() => handleSortClick(header)}
                              cursor={
                                header.column.getCanSort()
                                  ? 'pointer'
                                  : 'default'
                              }
                            >
                              {defaultSearchParameters.sort[0]?.field ===
                              header.id ? (
                                <DirectionIcon
                                  type={'alpha'}
                                  direction={
                                    defaultSearchParameters.sort[0]
                                      ?.direction || 'asc'
                                  }
                                  style={{
                                    color:
                                      defaultSearchParameters.sort[0]?.field ===
                                      header.id
                                        ? colors.primary900
                                        : colors.neutral300,
                                  }}
                                />
                              ) : (
                                <DirectionIcon
                                  type={'alpha'}
                                  direction={'normal'}
                                  style={{
                                    color:
                                      defaultSearchParameters.sort[0]?.field ===
                                      header.id
                                        ? colors.primary900
                                        : colors.neutral300,
                                  }}
                                />
                              )}
                            </Box>
                          </Flex>
                        ) : (
                          <Text className="users-table__header-text" mr={2}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </Text>
                        )}
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map(row => {
                return (
                  <Tr
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    bg={row.id === selectedRowId ? 'gray.100' : 'transparent'}
                    color={colors.slate}
                    _hover={{
                      bg: colors.lighterGrey,
                      textColor: colors.deepSlate2,
                    }}
                    cursor="pointer"
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      const textAlign =
                        cell.column.columnDef.textAlign || 'left';

                      return (
                        <Td
                          className="users-table__body-cell"
                          px={index === 0 ? '0px' : '15px'}
                          py="0px"
                          key={cell.id}
                          textAlign={textAlign}
                        >
                          {index === 0 ? (
                            <Box className="users-table__first-cell-container">
                              <Box
                                className="users-table__indicator"
                                bg={`${
                                  row.original.inactive_at
                                    ? 'gray.400'
                                    : 'green.400'
                                }`}
                              ></Box>
                              <Box className="users-table__first-cell">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Box>
                            </Box>
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
      <Pagination
        table={table}
        data={usersData}
        pagination={pagination}
        setPagination={setPagination}
        setSelectedRowId={setSelectedRowId}
      />
    </Box>
  );
};
